import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Send } from "lucide-react";

export default function ChefNotifications() {
  const [items, setItems] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", message: "", to_salarie_id: "all" });

  const load = async () => {
    try {
      const [n, s] = await Promise.all([
        api.get("/chef/notifications"),
        api.get("/chef/salaries"),
      ]);
      setItems(n.data);
      setSalaries(s.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { title: form.title, message: form.message };
      if (form.to_salarie_id && form.to_salarie_id !== "all") payload.to_salarie_id = form.to_salarie_id;
      await api.post("/chef/notifications", payload);
      toast.success("Message envoyé");
      setOpen(false);
      setForm({ title: "", message: "", to_salarie_id: "all" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Messagerie"
        title="Messages aux salariés"
        sub="Diffusez des annonces à toute l'équipe ou des messages ciblés."
        right={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="chef-new-notif-btn" className="h-11 bg-[#002FA7] hover:bg-[#002175] text-white font-semibold">
                <Plus className="h-4 w-4 mr-2" /> Nouveau message
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader><DialogTitle className="font-display text-2xl font-black tracking-tight">Envoyer un message</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="chef-notif-form">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Destinataire</Label>
                  <Select value={form.to_salarie_id} onValueChange={(v) => setForm({ ...form, to_salarie_id: v })}>
                    <SelectTrigger data-testid="notif-to" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les salariés</SelectItem>
                      {salaries.map((s) => <SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Titre</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="notif-title" className="h-11" required />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Message</Label>
                  <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="notif-message" rows={5} required />
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="notif-submit" className="bg-[#002FA7] hover:bg-[#002175] text-white">
                    <Send className="h-4 w-4 mr-2" /> Envoyer
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="p-6 lg:p-10 space-y-3" data-testid="chef-notif-list">
        {items.length === 0 ? (
          <div className="bg-white border border-[#DEE2E6] p-8 text-center text-sm text-[#868E96]">Aucun message.</div>
        ) : items.map((n) => {
          const s = salaries.find((x) => x.id === n.to_user_id);
          return (
            <div key={n.id} className="bg-white border border-[#DEE2E6] p-5" data-testid={`notif-${n.id}`}>
              <div className="flex justify-between items-start mb-2">
                <div>
                  <div className="font-display font-bold">{n.title}</div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#868E96] font-semibold mt-1">
                    À : {s ? `${s.prenom} ${s.nom}` : n.to_user_id ? "Avertissement reçu" : "Tous les salariés"}
                  </div>
                </div>
                <div className="text-xs text-[#868E96]">{new Date(n.created_at).toLocaleString("fr-FR")}</div>
              </div>
              <p className="text-sm text-[#495057] whitespace-pre-wrap">{n.message}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
